import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { trackEvent } from "../lib/analytics";
import WorkModal from "./WorkModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function resolveUrl(url) {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function Work() {
  const { t, lang } = useLang();
  const [projects, setProjects] = useState([]);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    fetch(`${API}/projects`)
      .then((r) => r.json())
      .then((data) => setProjects(Array.isArray(data) ? data : []))
      .catch(() => setProjects([]));
  }, []);

  const activeProject = projects.find((p) => p.id === activeId) || null;

  const goToContact = () => {
    setActiveId(null);
    setTimeout(() => {
      document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  return (
    <section
      id="work"
      data-testid="work-section"
      className="relative py-24 md:py-40 border-b border-[#1a1a1a]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 md:mb-24">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-4 mb-6">
              <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#ff4500]">
                {t.work.label}
              </span>
              <span className="h-[1px] w-16 bg-[#262626]" />
            </div>
            <h2
              data-testid="work-heading"
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight"
            >
              {t.work.headingA} <span className="italic text-[#ff4500]">{t.work.headingB}</span>
              <br />
              {t.work.headingC}
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-8 flex items-end">
            <p className="text-neutral-400 text-base md:text-lg leading-relaxed">
              {t.work.intro}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-x-10 md:gap-y-20">
          {projects.map((p, idx) => {
            const role = lang === "pt" ? p.role_pt || p.role_en : p.role_en;
            const shortDesc = lang === "pt" ? p.description_pt || p.description_en : p.description_en;
            // Editorial layout: first project full-width landscape hero, rest square 1:1 crop
            const layoutSpan = idx === 0 ? "md:col-span-12" : "md:col-span-6";
            const layoutHeight = idx === 0
              ? "h-[75vh] md:h-[90vh]"
              : "aspect-square";
            const mediaFit = idx === 0 ? "object-contain" : "object-cover";
            const media = p.video_url ? (
              <video
                src={resolveUrl(p.video_url)}
                autoPlay
                muted
                loop
                playsInline
                className={`work-card-image absolute inset-0 w-full h-full ${mediaFit} object-center`}
              />
            ) : (
              <img
                src={resolveUrl(p.image_url)}
                alt={`${p.client} — ${p.title}`}
                className={`work-card-image absolute inset-0 w-full h-full ${mediaFit} object-center`}
                loading="lazy"
              />
            );
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  setActiveId(p.id);
                  trackEvent("pageview", { path: `/case/${p.key}` });
                }}
                data-testid={`work-card-${p.key}`}
                className={`work-card group relative text-left flex flex-col ${layoutSpan}`}
              >
                {/* Media area */}
                <div className={`relative overflow-hidden bg-[#121212] ${layoutHeight}`}>
                  {media}
                  <div className="absolute top-5 left-5 flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] bg-black/60 backdrop-blur-md text-white px-2.5 py-1 border border-white/10">
                      {p.client}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
                      {p.year}
                    </span>
                  </div>
                  <div className="work-overlay absolute inset-0 bg-black/55 backdrop-blur-[2px] flex flex-col justify-end p-6 md:p-10">
                    <div className="flex items-end justify-between gap-6 max-w-5xl">
                      <div className="flex-1">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#ff4500] mb-3">
                          {role}
                        </div>
                        <h3 className={`font-serif font-light leading-[0.95] tracking-tight text-white ${idx === 0 ? "text-4xl md:text-6xl lg:text-7xl" : "text-3xl md:text-5xl"}`}>
                          {p.title}
                        </h3>
                        {shortDesc && (
                          <p className="mt-4 text-neutral-200 text-base md:text-lg leading-relaxed max-w-2xl line-clamp-4">
                            {shortDesc}
                          </p>
                        )}
                        <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-white/80 inline-flex items-center gap-2">
                          {lang === "pt" ? "Ver case study" : "Open case study"} <ArrowUpRight size={14} />
                        </div>
                      </div>
                      <span className="w-14 h-14 flex-shrink-0 bg-[#ff4500] flex items-center justify-center">
                        <ArrowUpRight size={22} className="text-black" />
                      </span>
                    </div>
                  </div>
                </div>

                {/* Always-visible editorial caption */}
                <div className="pt-6 pb-2 flex items-start justify-between gap-6">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">
                      <span className="text-[#ff4500]">{String(idx + 1).padStart(2, "0")}</span>
                      <span className="h-[1px] w-6 bg-[#262626]" />
                      <span>{p.client}</span>
                      <span className="w-1 h-1 rounded-full bg-neutral-700" />
                      <span>{p.year}</span>
                    </div>
                    <h4 className={`font-serif font-light tracking-tight text-white group-hover:text-[#ff4500] transition-colors duration-300 leading-tight ${idx === 0 ? "text-2xl md:text-4xl" : "text-xl md:text-2xl lg:text-3xl"}`}>
                      {p.title}
                    </h4>
                    {role && (
                      <p className="mt-2 text-neutral-400 text-sm md:text-base leading-relaxed">
                        {role}
                      </p>
                    )}
                  </div>
                  <span className="w-10 h-10 flex-shrink-0 border border-[#262626] group-hover:border-[#ff4500] group-hover:bg-[#ff4500] flex items-center justify-center transition-all duration-300 mt-1">
                    <ArrowUpRight size={14} className="text-white" />
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex justify-center mt-16">
          <a
            href="https://www.studionuts.com.br/work"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="work-view-all-button"
            className="btn-outline-orange inline-flex items-center gap-3 px-7 py-4 text-[12px] uppercase tracking-[0.25em] font-medium"
          >
            <span>{t.work.viewAll}</span>
            <ArrowUpRight size={16} />
          </a>
        </div>
      </div>

      <WorkModal project={activeProject} onClose={() => setActiveId(null)} onGoToContact={goToContact} />
    </section>
  );
}
